import * as React from "react";
// eslint-disable-next-line no-restricted-imports
import moment from "moment";

import type { DashboardOutput } from "../utils/fetch-api";
import { getDashboardOutput } from "../utils/fetch-api";
import { Dimensions, MetricTypes } from "../autogen/metrics";
import type { DashboardMetadata } from "../utils/fetch-dashboard-metadata";
import type { CustomTimeRange } from "../utils/types";

const SALES_VEL_DAYS = 30;

export const getTotalSalesMetric = (metadata: DashboardMetadata) => {
  return metadata.supports_refunds_by_refunded_date
    ? MetricTypes.GROSS_PRODUCT_REVENUE_FROM_ORDER_PROPORTION_LESS_DISCOUNTS_LESS_REFUNDED_AMOUNT_FROM_REFUNDS
    : MetricTypes.GROSS_PRODUCT_REVENUE_FROM_ORDER_PROPORTION_LESS_DISCOUNTS_LESS_REFUNDED_AMOUNT_FROM_ORDERS;
};

export const getReturnRateMetric = (metadata: DashboardMetadata): MetricTypes | null => {
  if (metadata.supports_refunds_by_refunded_date || metadata.supports_item_level_refund_information) {
    return metadata.supports_refunds_by_refunded_date
      ? MetricTypes.ITEM_LEVEL_RETURN_RATE_FROM_REFUNDS
      : MetricTypes.ITEM_LEVEL_RETURN_RATE_FROM_PRODUCTS;
  }
  return null;
};

export const getNewCustomerPercentMetric = (metadata: DashboardMetadata): MetricTypes => {
  if (metadata.supports_item_level_refund_information) {
    return MetricTypes.ITEM_LEVEL_NEW_CUSTOMER_PERCENT_FROM_PRODUCTS;
  }
  return MetricTypes.ITEM_LEVEL_NEW_CUSTOMER_PERCENT;
};

export const getRefundsMetric = (metadata: DashboardMetadata): MetricTypes | null => {
  if (metadata.supports_refunds_by_refunded_date || metadata.supports_item_level_refund_information) {
    return metadata.supports_refunds_by_refunded_date
      ? MetricTypes.PRODUCT_REFUNDS_FROM_REFUNDS
      : MetricTypes.PRODUCT_REFUNDS_FROM_PRODUCTS;
  }
  return null;
};

export const getTotalQuantityMetric = (metadata: DashboardMetadata) => {
  return metadata.supports_refunds_by_refunded_date
    ? MetricTypes.GROSS_ITEM_QUANTITY_LESS_REFUNDED_QUANTITY_FROM_REFUNDS
    : MetricTypes.GROSS_ITEM_QUANTITY_LESS_REFUNDED_QUANTITY_FROM_PRODUCTS;
};

export const addExtraProductsData = (
  data: DashboardOutput,
  salesVelocityOutput: DashboardOutput,
  metaData: DashboardMetadata,
  selectedTimeRange: CustomTimeRange
): DashboardOutput => {
  const totalQuantityMetric = getTotalQuantityMetric(metaData);
  const days = selectedTimeRange.end.diff(selectedTimeRange.start, "days") + 1;

  const salesVelMap = salesVelocityOutput.data.reduce((rv, productRow) => {
    rv[productRow[Dimensions.PRODUCT]] = +(productRow[totalQuantityMetric] ?? 0) / SALES_VEL_DAYS;
    return rv;
  }, {});

  let totalVelocity = 0;
  let totalSales = 0;

  data.data.forEach((dataPoint) => {
    const averageUnitsRaw = +(dataPoint[totalQuantityMetric] ?? 0) / days;
    const salesVelocityRaw = salesVelMap[dataPoint[Dimensions.PRODUCT]] || 0;

    const averageUnits = averageUnitsRaw >= 0 ? averageUnitsRaw : 0;
    const salesVelocity = salesVelocityRaw >= 0 ? salesVelocityRaw : 0;

    dataPoint[MetricTypes.AVERAGE_DAILY_UNITS] = averageUnits;
    dataPoint[MetricTypes.SALES_VELOCITY_THIRTY_DAYS] = salesVelocity;

    totalVelocity = totalVelocity + salesVelocity;
    totalSales = totalSales + averageUnits;
  });

  data.summary[MetricTypes.AVERAGE_DAILY_UNITS] = totalSales;
  data.summary[MetricTypes.SALES_VELOCITY_THIRTY_DAYS] = totalVelocity;

  return data;
};

export const useSalesVelocityData = (dashboardDataPath: string, metaData: DashboardMetadata | undefined) => {
  const [data, setData] = React.useState<DashboardOutput | null>(null);

  React.useEffect(() => {
    if (metaData) {
      getDashboardOutput(
        dashboardDataPath,
        [
          {
            metrics: [getTotalQuantityMetric(metaData)],
            filename: "products_insights",
            groupBys: [Dimensions.PRODUCT],
          },
        ],
        getSalesVelocityRange(),
        (data) => {
          setData(data);
        },
        Dimensions.PRODUCT,
        (error) => {
          // eslint-disable-next-line no-console
          console.log(error);
        }
      );
    }
  }, [dashboardDataPath, metaData]);

  return data;
};

const getSalesVelocityRange = () => {
  return { start: moment().subtract(SALES_VEL_DAYS + 1, "day"), end: moment().subtract(1, "days") };
};
